'use client';

import React from 'react';
import { X, ChevronLeft } from 'lucide-react';
import type { EntryTypeConfig } from './expense';
import type { ChildEntryType } from '../types';

export interface EntryFormHeaderProps {
  config: EntryTypeConfig;
  selectedEntryType: ChildEntryType;
  entryTypeProp?: ChildEntryType;
  titleOverride?: string;
  isJobLinkedExpense: boolean;
  submitting: boolean;
  onBackToBeginAction?: () => void;
  onCancel: () => void;
  onBackToTypeSelection: () => void;
  /** Hide the X close button (e.g. in public upload mode where there's nowhere to cancel to). */
  hideCloseButton?: boolean;
}

export function EntryFormHeader({
  config,
  selectedEntryType,
  entryTypeProp,
  titleOverride,
  isJobLinkedExpense,
  submitting,
  onBackToBeginAction,
  onCancel,
  onBackToTypeSelection,
  hideCloseButton = false,
}: EntryFormHeaderProps) {
  const Icon = config.icon;
  const showBack = selectedEntryType !== entryTypeProp || onBackToBeginAction;

  return (
    <div className="shadow-sm flex-shrink-0 bg-white dark:bg-gray-800 transition-[padding] duration-200">
      <div className="max-w-4xl mx-auto px-3 py-3 relative">
        {showBack && (
          <button
            type="button"
            onClick={() => {
              if (onBackToBeginAction) {
                onBackToBeginAction();
              } else {
                onBackToTypeSelection();
              }
            }}
            disabled={submitting}
            className="absolute left-3 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        {!hideCloseButton && (
          <button
            onClick={onCancel}
            disabled={submitting}
            className="absolute right-3 top-3 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="text-center pt-1">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-14 sm:w-14">
            <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
            {selectedEntryType !== entryTypeProp
              ? config.label
              : (titleOverride ?? (isJobLinkedExpense && selectedEntryType !== 'casual_wage' ? 'Job linked Expense' : config.label))}
          </h1>
        </div>
      </div>
    </div>
  );
}
